import React, { useCallback, useState } from 'react';

import toursApi from '../api';
import { useGet, useGetOne, usePost, usePut, useRemove, useSoftDelete } from '../../apiHooks';
import { Tour } from '../types';

import Context, { ToursContext } from './context';

function ToursProvider({ children }: { children: React.ReactNode }) {
    const [data, setData] = useState<ToursContext['data']>([]);
    const [selected, setSelected] = useState<Tour | null>(null);
    const [error, setError] = useState<ToursContext['error']>({});
    const handleError = useCallback((err: ToursContext['error']) => {
        setError(err);
        throw err;
    }, []);

    const handleGetData = useCallback((res: ToursContext['data']) => {
        setData(res);
        return res;
    }, []);
    const get = useGet(toursApi, data, handleGetData, setError);

    const getOne = useGetOne(toursApi, setSelected, setError);

    const handleSetData = useCallback((_item: never, newData: ToursContext['data']) => {
        setData(newData);
    }, []);
    const post = usePost(toursApi, data, handleSetData, setError);

    const put = usePut(toursApi, data, handleSetData, setError);

    const duplicateManyByFromDate = useCallback<ToursContext['duplicateManyByFromDate']>(
        (id, body) => {
            return toursApi
                .duplicateManyByFromDate(id, body)
                .then((tours: Tour[]) => {
                    const newData = data.concat(tours);
                    setData(newData);
                    return tours;
                })
                .catch(handleError);
        },
        [data, handleError]
    );

    const handleDeleteCallback = useCallback((id: string | number, newData: ToursContext['data']) => {
        setData(newData);
        setSelected(null);
        return id;
    }, []);
    const remove = useRemove(toursApi, data, handleDeleteCallback, setError);

    const softDelete = useSoftDelete(toursApi, data, handleDeleteCallback, setError);

    const query = useCallback<ToursContext['query']>(
        (query, options) => {
            return toursApi
                .query(query, options)
                .then((res) => res)
                .catch(handleError);
        },
        [handleError]
    );

    const getPartialFormat = useCallback<ToursContext['getPartialFormat']>(
        async (params, options) => {
            try {
                const res = await toursApi.getPartialFormat(params, options);
                return res;
            } catch (err) {
                return handleError(err);
            }
        },
        [handleError]
    );
    return (
        <Context.Provider
            value={{
                get,
                getOne,
                post,
                put,
                remove,
                softDelete,
                data,
                setData,
                selected,
                setSelected,
                error,
                duplicateManyByFromDate,
                query,
                getPartialFormat
            }}
        >
            {children}
        </Context.Provider>
    );
}

export default ToursProvider;
